import React from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { navigate, RouteComponentProps } from '@reach/router';
import styled from 'styled-components';
import { appDispatch, IAppInstance, IUsers, RootState } from 'store/store';
import {
  requireProviderTags,
  requireProviderTagsFn,
} from 'store/providers/providerSlice';
import { IOption } from 'components/Select/Select';
import messages from './Messages';
import { display } from 'components/ErrorView/ErrorDialog';
import Button from 'components/Button';
import Input from 'components/Input';
import Select from 'components/Select';
import SurveyManagerModal from 'components/FileManager/ItemsViewer/Survey/SurveyManagerModal';
import OpenItem from 'components/FileManager/DataClass/OpenItem';
import LogoLoading from 'components/LogoLoading';
import TemplateAdminModal from './TemplateAdminModal';
import notify from 'components/Notify';
import { IProviders, ITags, ITagView } from 'store/providers/providerTypes';
import {
  changeTemplateAdmin,
  changeTemplateAdminFn,
  IProviderTemplate,
  IProviderTemplates,
  ITemplateAdmin,
  requireTemplate,
  requireTemplateFn,
  saveProviderTemplate,
  saveProviderTemplateFn,
} from 'store/templates/templatesSlice';
import {
  requireSeveralUser,
  requireSeveralUserFn,
  searchUser,
  searchUserFn,
} from 'store/users/usersSlice';
import {
  simpleRequireSurvey,
  simpleRequireSurveyFn,
} from 'store/survey/surveySlice';

const JSSurveyManagerModal: any = SurveyManagerModal; // ToDo: Remove this line when the component rewrote with TS

interface IStateToProps {
  appInstance: IAppInstance;
  providerId: number;
  tags: ITags | null;
  templates: IProviderTemplates;
  thisProvider: IProviders['map'][0];
  users: IUsers;
}

interface IDispatchToProps {
  changeTemplateAdmin: (
    adminView: ITemplateAdmin,
    isRemoveAction: boolean
  ) => ReturnType<typeof changeTemplateAdminFn>;
  requireProviderTags: ({
    providerId,
  }: {
    providerId: number;
  }) => ReturnType<typeof requireProviderTagsFn>;
  requireSeveralUser: (
    usersList: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
  requireTemplate: (templateId: number) => ReturnType<typeof requireTemplateFn>;
  saveProviderTemplate: (
    template: IProviderTemplate,
    isUpdate: boolean
  ) => ReturnType<typeof saveProviderTemplateFn>;
  searchUser: (phoneNumber: string) => ReturnType<typeof searchUserFn>;
  simpleRequireSurvey: (
    surveyToken: string
  ) => ReturnType<typeof simpleRequireSurveyFn>;
}

interface IOwnProps {
  templateId?: string;
}

type IComponentProps = IOwnProps &
  IDispatchToProps &
  IStateToProps &
  RouteComponentProps;

interface IComponentStates {
  askLocation: boolean;
  askProfilePhoto: boolean;
  askSocialInfo: boolean;
  askSelfDocuments: boolean;
  askRelatedDocs: boolean;
  description: string;
  isSaving: boolean;
  loading: boolean;
  name: string;
  openAdminModal: boolean;
  openItem?: any;
  openItemId?: number;
  required: boolean;
  single: boolean;
  surveyLoading: boolean;
  tag: IOption[];
}

const Section = styled('div')`
  margin: 1rem 0;
`;

const FormActions = styled(Section)`
  display: flex;
  justify-content: space-between;
`;

class ProviderTemplateEditor extends React.Component<
  IComponentProps,
  IComponentStates
> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      loading: true,
      name: '',
      description: '',
      tag: [],
      isSaving: false,
      askLocation: true,
      askProfilePhoto: true,
      askSocialInfo: true,
      askSelfDocuments: true,
      askRelatedDocs: true,
      single: false,
      required: false,
      openItemId: undefined,
      openItem: undefined,
      surveyLoading: false,
      openAdminModal: false,
    };
  }

  componentDidMount() {
    this.props
      .requireSeveralUser([])
      .then(() =>
        this.props.requireProviderTags({
          providerId: this.props.thisProvider.data!.id,
        })
      )
      .then(() => {
        const templateId = this.props.templateId;
        if (templateId) {
          this.props
            .requireTemplate(Number(templateId))
            .then(({ template }: { template: IProviderTemplate }) => {
              let tag: IOption[] = [];
              if (template.tag) {
                tag = [{ value: template.tag.id, label: template.tag.text }];
              }
              this.setState({
                name: template.name,
                description: template.description || '',
                tag,
                askLocation: template.askLocation,
                askProfilePhoto: template.askProfilePhoto,
                askSocialInfo: template.askSocialInfo,
                askSelfDocuments: template.askSelfDocuments,
                askRelatedDocs: template.askRelatedDocs,
                single: template.single,
                required: template.required,
              });
              const templateAdmins = Object.values(
                template.admins as ITemplateAdmin[]
              ).reduce((list: number[], admin) => {
                list.push(admin.userId);
                return list;
              }, []);
              this.props.requireSeveralUser(templateAdmins).then(() => {
                this.setState({ loading: false });
              });
            });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch((err: any) => {
        display(err);
      });
  }

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;
    if (name === 'name') {
      this.setState({ name: value });
    } else if (name === 'description') {
      this.setState({ description: value });
    }
  };

  handleChangeTags = (tag: IOption[]) => {
    this.setState({ tag });
  };

  handleChangeCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    if (name === 'askLocation') {
      this.setState({ askLocation: e.target.checked });
    } else if (name === 'askProfilePhoto') {
      this.setState({ askProfilePhoto: e.target.checked });
    } else if (name === 'askSocialInfo') {
      this.setState({ askSocialInfo: e.target.checked });
    } else if (name === 'askSelfDocuments') {
      this.setState({ askSelfDocuments: e.target.checked });
    } else if (name === 'askRelatedDocs') {
      this.setState({ askRelatedDocs: e.target.checked });
    } else if (name === 'single') {
      this.setState({ single: e.target.checked });
    } else if (name === 'required') {
      this.setState({ required: e.target.checked });
    }
  };

  handleSaveTemplate = (e: React.FormEvent) => {
    e.preventDefault();
    this.setState({ isSaving: true });
    let tag: ITagView | undefined;
    if (this.state.tag.length > 0) {
      const [firstTag] = this.state.tag;
      const tagItem = this.props.tags!.data[firstTag.value!]!;
      tag = {
        id: Number(tagItem.id),
        text: tagItem.text,
        parentId: tagItem.parentId,
      };
    }
    // @ts-expect-error need attention
    const template: IProviderTemplate = {
      name: this.state.name,
      tag,
      appInstanceId: this.props.appInstance.id!,
      askLocation: this.state.askLocation,
      askProfilePhoto: this.state.askProfilePhoto,
      askSocialInfo: this.state.askSocialInfo,
      askSelfDocuments: this.state.askSelfDocuments,
      askRelatedDocs: this.state.askRelatedDocs,
      single: this.state.single,
      required: this.state.required,
    };
    if (this.state.description.length > 0) {
      template.description = this.state.description;
    }

    const isUpdate = !!this.props.templateId;
    if (isUpdate) {
      template.id = Number(this.props.templateId);
    }
    this.props
      .saveProviderTemplate(template, isUpdate)
      .then(({ templateId }) => {
        this.setState({ isSaving: false });
        notify(messages.providerTemplateSaved(isUpdate));
        if (!isUpdate) {
          const providerId = this.props.providerId;
          navigate(`/content/${providerId}/templates/editor/${templateId}`);
        }
      })
      .catch((err: any) => {
        display(err);
        this.setState({ isSaving: false });
      });
  };

  onCancelTemplateEditing = () => {
    const providerId = this.props.providerId;
    navigate(`/content/${providerId}/templates`);
  };

  onOpenSurvey = () => {
    const templateId = this.props.templateId;
    if (templateId) {
      const template = this.props.templates.data[Number(templateId)];
      const templateSurvey = template.providerInfo;
      this.setState({ surveyLoading: true });
      this.props
        .simpleRequireSurvey(templateSurvey.editToken)
        .then(({ surveyId }) => {
          const item = {
            data: {
              allowComment: false,
              allowLike: false,
              description: null,
              editToken: template.providerInfo.editToken,
              editable: true,
              fillToken: template.providerInfo.fillToken,
              instance: null,
              name: template.name,
              providerId: this.props.providerId,
              status: 'exists',
              surveyId: template.providerInfo.id,
              surveyName: template.providerInfo.name,
              tags: [],
              type: 'survey',
            },
            state: 'loaded',
          };
          const openItem = new OpenItem(item, 'edit');
          this.setState({
            surveyLoading: false,
            openItemId: surveyId,
            openItem,
          });
        })
        .catch((err: any) => {
          display(err);
          this.setState({ surveyLoading: false });
        });
    }
  };

  changeState = (status: string) => {
    this.setState((prevState) => {
      const openItem = prevState.openItem;
      openItem.status = status;
      return { openItem };
    });
  };

  handleCloseSurveyModal = () => {
    this.setState({ openItem: undefined, openItemId: undefined });
  };

  handleOpenAdminModal = () => {
    this.setState((prevState) => ({
      openAdminModal: !prevState.openAdminModal,
    }));
  };

  handleChangeAdmin = (type: string, adminId: number) => {
    const admin: ITemplateAdmin = {
      userId: adminId,
      templateId: Number(this.props.templateId),
    };
    this.props
      .changeTemplateAdmin(admin, type === 'remove')

      .then(() => {
        if (type === 'remove') {
          notify(messages.adminRemoved);
        } else if (type === 'add') {
          notify(messages.adminAdded);
        }
      });
  };

  render() {
    const { tags, templateId, templates, users } = this.props;
    if (this.state.loading) {
      return <LogoLoading />;
    }
    const tagItems: IOption[] = Object.values(tags!.data!).reduce(
      (items: IOption[], tag: ITagView) => {
        const item: IOption = { value: tag.id, label: tag.text };
        items.push(item);
        return items;
      },
      []
    );

    const isUpdate = !!templateId;

    return (
      <div className="section">
        <form onSubmit={this.handleSaveTemplate}>
          <Input
            type="text"
            id="name"
            name="name"
            label={messages.title}
            placeholder={messages.title}
            onChange={this.handleChange}
            required={true}
            value={this.state.name}
          />
          <Input
            type="text"
            id="description"
            name="description"
            label={messages.description}
            placeholder={messages.description}
            onChange={this.handleChange}
            value={this.state.description}
          />
          <Select
            onChangeValues={this.handleChangeTags}
            values={this.state.tag}
            options={tagItems}
            placeholder={messages.tag}
            label={messages.tag}
          />
          <Section>
            <div className="row">
              <Input
                type="checkbox"
                onChange={this.handleChangeCheckbox}
                id="askLocation"
                name="askLocation"
                label={messages.askLocation}
                checked={this.state.askLocation}
                className="col s12 m6"
              />
              <Input
                type="checkbox"
                onChange={this.handleChangeCheckbox}
                id="askProfilePhoto"
                name="askProfilePhoto"
                label={messages.askProfilePhoto}
                checked={this.state.askProfilePhoto}
                className="col s12 m6"
              />
            </div>
            <div className="row">
              <Input
                type="checkbox"
                onChange={this.handleChangeCheckbox}
                id="askSocialInfo"
                name="askSocialInfo"
                label={messages.askSocialInfo}
                checked={this.state.askSocialInfo}
                className="col s12 m6"
              />
              <Input
                type="checkbox"
                onChange={this.handleChangeCheckbox}
                id="askSelfDocuments"
                name="askSelfDocuments"
                label={messages.askSelfDocuments}
                checked={this.state.askSelfDocuments}
                className="col s12 m6"
              />
            </div>
            <div className="row">
              <Input
                type="checkbox"
                onChange={this.handleChangeCheckbox}
                id="askRelatedDocs"
                name="askRelatedDocs"
                label={messages.askRelatedDocs}
                checked={this.state.askRelatedDocs}
                className="col s12 m6"
              />
              <Input
                type="checkbox"
                onChange={this.handleChangeCheckbox}
                id="single"
                name="single"
                label={messages.single}
                checked={this.state.single}
                className="col s12 m6"
              />
            </div>
            <div className="row">
              <Input
                type="checkbox"
                onChange={this.handleChangeCheckbox}
                id="required"
                name="required"
                label={messages.required}
                checked={this.state.required}
                className="col s12 m6"
              />
            </div>
          </Section>
          {isUpdate && (
            <Section>
              <Button
                color="green-outline"
                onClick={this.onOpenSurvey}
                loading={this.state.surveyLoading}
              >
                {messages.editSurvey}
              </Button>
              <Button
                color="green-outline"
                onClick={this.handleOpenAdminModal}
                style={{ margin: '0 1rem' }}
              >
                {messages.editAdmins}
              </Button>
              <TemplateAdminModal
                searchUser={this.props.searchUser}
                admins={templates.data[Number(templateId)].admins}
                isActive={this.state.openAdminModal}
                onClose={this.handleOpenAdminModal}
                onChangeAdmin={this.handleChangeAdmin}
                users={users}
              />
            </Section>
          )}
          <FormActions>
            <Button type="submit" color="green" loading={this.state.isSaving}>
              {messages.save}
            </Button>
            <Button color="red-outline" onClick={this.onCancelTemplateEditing}>
              {messages.cancel}
            </Button>
          </FormActions>
        </form>
        {this.state.openItemId && (
          <JSSurveyManagerModal
            openItem={this.state.openItem}
            changeState={this.changeState}
            isOpen={true}
            onMinimize={() => {
              //
            }}
            withoutProviderSurvey={true}
            onClose={this.handleCloseSurveyModal}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state: RootState
) => {
  const providerId = state.thisUser.provider.id!;
  const appInstance = state.appInstance;
  const thisProvider = state.providers.map[providerId];
  const tags = state.providers.tags[providerId];
  const templates = state.templates;
  const users = state.users;

  return { thisProvider, appInstance, tags, templates, providerId, users };
};

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = {
  requireProviderTags: ({ providerId }) =>
    appDispatch(requireProviderTags({ providerId })),
  changeTemplateAdmin: (adminView: ITemplateAdmin, isRemoveAction: boolean) =>
    appDispatch(changeTemplateAdmin({ adminView, isRemoveAction })),
  requireTemplate: (templateId) => appDispatch(requireTemplate({ templateId })),
  requireSeveralUser: (usersList) =>
    appDispatch(requireSeveralUser({ usersList })),
  saveProviderTemplate: (template: IProviderTemplate, isUpdate: boolean) =>
    appDispatch(
      saveProviderTemplate({
        template,
        isUpdate,
      })
    ),
  simpleRequireSurvey: (surveyToken) =>
    appDispatch(simpleRequireSurvey({ surveyToken })),
  searchUser: (phoneNumber) =>
    appDispatch(searchUser({ phoneOrEmail: phoneNumber })),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProviderTemplateEditor);
